// Unified design package cache adapter.
// In Tauri desktop: persists to SQLite via Rust commands (the canonical
// implementation — see haspdesktop/src-tauri/src/db/designs.rs).
// In browser: uses IndexedDB (src/cache/indexdb.js), which mirrors the exact
// same per-file, id-keyed shape. See docs plan "Design Packages".

import {
    savePackage as idbSave,
    loadPackage as idbLoad,
    listPackages as idbList,
    deletePackage as idbDelete,
} from './indexdb.js'
import { serializeStorageValue } from '../utils/storageSerialization.js'

const isTauri = () => '__TAURI__' in window

async function invoke(cmd, args) {
    const { invoke: tauriInvoke } = await import('@tauri-apps/api/core')
    return tauriInvoke(cmd, args)
}

/**
 * @param {string} packageId   - manifest.id, the package's stable identity
 * @param {string} packagePath - storage detail (e.g. "designs/weather"), carried on each row
 * @param {Array<{filePath, content, encoding, mime}>} files
 */
export async function savePackage(packageId, packagePath, files) {
    const now = Date.now()
    const rows = serializeStorageValue(files.map(f => ({
        packageId,
        packagePath,
        // DesignPackage.toFileList() is the canonical interchange shape and
        // calls this field `path`; accept the row-shaped name as well for
        // callers that already have cache rows.
        filePath: f.filePath ?? f.path,
        content: f.content,
        encoding: f.encoding,
        mime: f.mime ?? null,
        cachedAt: now,
        localModified: f.localModified ?? false,
    })), '$.files')
    if (isTauri()) {
        await invoke('save_package_command', { packageId, files: rows })
    } else {
        await idbSave(packageId, rows)
    }
}

export async function loadPackage(packageId) {
    if (isTauri()) return invoke('load_package_command', { packageId })
    return idbLoad(packageId)
}

/** One row per cached package (its manifest.json row) — a lightweight list. */
export async function listPackages() {
    if (isTauri()) return invoke('list_packages_command')
    return idbList()
}

export async function deletePackage(packageId) {
    if (isTauri()) {
        await invoke('delete_package_command', { packageId })
    } else {
        await idbDelete(packageId)
    }
}
